import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ArrowDownIcon,
  CheckCircleOutlineIcon,
  CircleCloseIcon,
  CloseIcon,
  ForbiddenIcon,
  LockIcon,
  PenIcon,
  SearchFileIcon,
  SortingArrowIcon,
  TableButtonsIcon,
  ViewIcon,
} from '@src/svg';
import './table.scss';
import TableSkeleton from '@uikit/Loader/TableSkeleton/TableSkeleton';
import {
  BIG_TABLE_DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
  FALLBACK_IMAGE,
  IMAGE_STORAGE_URL,
  SURVEY_TEMPLATE_DEFAULT_PAGE_SIZE,
} from '@src/utils/constants';
import { useAppDispatch } from '@store/hooks';
import TruncatedText from '@component/TruncatedText/TruncatedText';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import useIsSurveyTemplatesPage from '@hooks/useIsSurveyTemplatesPage';
import { PermissionsEnum, TableProps } from './constants';
import Pagination from '@uikit/Pagination/Pagination';
import CustomDropdown from '@uikit/CustomDropdown/CustomDropdown';
import { idType } from '@src/types';
import { Column } from '@uikit/Tables/types';
import { useForm } from '@hooks/useDirtyForm';
import { MonitoringRideGridTableOptions } from '@component/Monitoring/constants';
import { ControlledCheckbox } from '@uikit/FormItems/ControlledItems';
import { handleImgError } from '@src/utils';
import { logger } from '@utils/logger';
import { StatusWrapper } from '@component/StatusWrapper/StatusWrapper';
import { normalizeOnlineStatus } from '@utils/helpers';

export const PERMISSIONS_TOGGLE_STORAGE_KEY = 'PTA';
export const MICROSOFT_TOGGLE_STORAGE_KEY = 'MSB';

const TableDynamic: FC<TableProps> = ({
  isLoading = false,
  arrowIconMember = false,
  modalName,
  path,
  totalPages,
  page = 0,
  totalElements,
  size,
  columns,
  onEditClick,
  onAuditClick,
  onImageClick,
  onCheckboxClick,
  onPageChange,
  className,
  abilities,
  activeRowId,
  handleSelectedRow,
  handleDeleteRow,
  rows,
  onFieldClick,
  tableName,
  requestTablePageChange,
  openModal,
  tableCustomClassName,
  tableTitle,
  columnsSorting,
  sortingTable,
  setSortingTable,
  hasPagination = true,
  method,
  argument,
  params,
  skeletonProps,
  tableIsBig,
  statusSetter,
  handleUpdatePermission,
  dataTestId,
  scrollToTopOnDataChange = true,
}) => {
  const dispatch = useAppDispatch();

  // Refs for the table container and the TablePopup component
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const isResizingRef = useRef<boolean>(false);

  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [updatedPage, setPage] = useState<number>(page);
  const [columnOrder, setColumnOrder] = useState(columns);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<idType>(activeRowId || null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isDraggingColumn, setIsDraggingColumn] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hoveredCol, setHoveredCol] = useState<string | null>(null);

  const { control, watch, reset } = useForm<MonitoringRideGridTableOptions>({
    defaultValues: {},
  });

  const isSurveyTemplate = useIsSurveyTemplatesPage();

  useEffect(() => {
    setSelectedItem(activeRowId || null);
  }, [activeRowId]);

  useEffect(() => {
    if (columns.some((el) => el.headerName === 'checkboxes')) {
      const defaultValues = {};
      columns.forEach((el) => {
        if (el.field) {
          defaultValues[el.field] = el.isChecked;
        }
      });
      reset(defaultValues);
    }
  }, []);

  useEffect(() => {
    const subscription = watch((value) => {
      setColumnOrder(
        columns.filter((column) => !column.field || value[column.field])
      );
    });

    return () => subscription.unsubscribe();
  }, [columns, watch]);

  const onColumnDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const buttonsIndex = columnOrder.findIndex((col) => col.type === 'buttons');

    const lastDraggableIndex =
      buttonsIndex === -1 ? Infinity : buttonsIndex - 1;

    if (destination.index > lastDraggableIndex) return;

    const updatedColumns = Array.from(columnOrder);
    const [removed] = updatedColumns.splice(source.index, 1);
    updatedColumns.splice(destination.index, 0, removed);

    setColumnOrder(updatedColumns);
  };

  useEffect(() => {
    setColumnOrder(
      columns.some((el) => el.headerName === 'checkboxes')
        ? columns.filter((el) => el.isChecked)
        : columns
    );
  }, [columns]);

  useEffect(() => {
    setPage(page);
  }, [page]);

  useEffect(() => {
    if (scrollToTopOnDataChange && tableScrollRef.current) {
      tableScrollRef.current.scrollTop = 0;
    }
  }, [rows, scrollToTopOnDataChange]);

  const getInitialRowsPerPage = () =>
    tableTitle === 'Service Type'
      ? BIG_TABLE_DEFAULT_PAGE_SIZE
      : isSurveyTemplate
        ? SURVEY_TEMPLATE_DEFAULT_PAGE_SIZE
        : DEFAULT_PAGE_SIZE;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        tableContainerRef.current &&
        !tableContainerRef.current.contains(event.target as Node)
      ) {
        setIsPopupVisible(false);
      }
    };
    if (isPopupVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupVisible]);

  const handleResizeEnd = () => {
    isResizingRef.current = false;
    setIsResizing(false);
  };

  useEffect(() => {
    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        setIsResizing(false);
      }
    };

    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // need check
  const showPagination =
    hasPagination &&
    !isNaN(page || 0) &&
    !isNaN(totalElements || 0) &&
    !isNaN(size || 0) &&
    !isNaN(totalPages || 0);

  const hasTableData = rows && rows.length > 0;
  const showInitialSkeleton = isLoading && !hasTableData;

  const handlePageChange = async (operator: boolean) => {
    setIsUpdating(true);
    let newPage = operator ? updatedPage + 1 : updatedPage - 1;

    try {
      if (requestTablePageChange) {
        await dispatch(
          requestTablePageChange.initiate(
            {
              requestId: Date.now(),
              params: {
                page: newPage,
                size: size,
                ...params,
                ...sortingTable,
              },
              pathName: path,
              type: path,
              id: 2,
              argument: argument ?? {},
              ...(argument ? argument : {}),
              body: tableName ? columnsSorting[tableName] : {},
            },
            { forceRefetch: true }
          )
        );
      } else if (onPageChange) {
        onPageChange(newPage);
      }
      setPage(newPage);
    } catch (err) {
      logger.log(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSizeChange = async (changedSize: number) => {
    setIsUpdating(true);
    setPage(0);
    try {
      if (requestTablePageChange) {
        await dispatch(
          requestTablePageChange.initiate(
            {
              requestId: Date.now(),
              params: {
                page: 0,
                size: changedSize,
                ...params,
                ...sortingTable,
              },
              pathName: path,
              type: path,
              id: 2,
              argument: argument ?? {},
              ...(argument ? argument : {}),
              body: tableName ? columnsSorting[tableName] : {},
            },
            { forceRefetch: true }
          )
        );
      }
    } catch (err) {
      logger.log(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (!requestTablePageChange) return;

    setIsRefreshing(true);

    try {
      await dispatch(
        requestTablePageChange.initiate(
          {
            requestId: Date.now(),
            params: {
              page: updatedPage,
              size: size,
              ...params,
              ...sortingTable,
            },
            pathName: path,
            type: path,
            id: 2,
            argument: argument ?? {},
            ...(argument ? argument : {}),
            body: tableName ? columnsSorting?.[tableName] : {},
          },
          { forceRefetch: true }
        )
      );
    } catch (err) {
      logger.log(err);
    } finally {
      setIsRefreshing(false);
    }
  }, [
    requestTablePageChange,
    dispatch,
    updatedPage,
    size,
    params,
    sortingTable,
    path,
    argument,
    tableName,
    columnsSorting,
  ]);

  const handleSorting = (col: Column) => {
    if (method === 'GET' || !col.isSortable) {
      return;
    }

    setSortingTable((prevSorting) => {
      const newSortDir =
        prevSorting.sortBy === col.sortingField &&
        prevSorting.sortDir === 'desc'
          ? 'asc'
          : 'desc';

      const updatedSorting = {
        sortBy: col.sortingField,
        sortDir: newSortDir,
      };

      requestTablePageChange &&
        dispatch(
          requestTablePageChange.initiate(
            {
              requestId: Date.now(),
              id: 2,
              pathName: path,
              type: path,
              argument: argument ?? {},
              ...(argument ? argument : {}),
              params: {
                page,
                size,
                ...params,
                ...updatedSorting,
              },
              body: tableName ? columnsSorting[tableName] : {},
            },
            { forceRefetch: true }
          )
        );

      return updatedSorting;
    });
  };

  // const handleResizeStart = (e: React.MouseEvent, field: string) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   isResizingRef.current = true;
  //   setIsResizing(true);
  //   const startX = e.clientX;
  //   const startWidth = columnWidths[field];
  //
  //   const onMouseMove = (moveEvent: MouseEvent) => {
  //     const newWidth = startWidth + (moveEvent.clientX - startX);
  //     setColumnWidths((prevWidths) => ({
  //       ...prevWidths,
  //       [field]: Math.max(newWidth, 50),
  //     }));
  //   };
  //
  //   const onMouseUp = () => {
  //     document.removeEventListener('mousemove', onMouseMove);
  //     document.removeEventListener('mouseup', onMouseUp);
  //   };
  //
  //   document.addEventListener('mousemove', onMouseMove);
  //   document.addEventListener('mouseup', onMouseUp);
  // };

  useEffect(() => {
    const initialWidths = columns?.reduce((acc, col) => {
      acc[col.field] = 150; // Default width, customize as needed
      return acc;
    }, {});
    setColumnWidths(initialWidths);
  }, [columns]);

  //TODO this part need for change permissions in front

  const value = window.localStorage.getItem(PERMISSIONS_TOGGLE_STORAGE_KEY);

  const isPermissionToggleEnabled = value ? JSON.parse(value) : false;

  const getPermissionAbilityId = (field: string) => {
    const permissionFieldMap: Partial<Record<string, PermissionsEnum>> = {
      read: PermissionsEnum.Read,
      create: PermissionsEnum.Create,
      edit: PermissionsEnum.Edit,
      audit: PermissionsEnum.Audit,
      document: PermissionsEnum.Special,
      special: PermissionsEnum.Special,
    };

    return permissionFieldMap[field.toLowerCase()];
  };

  const renderColField = ({
    field,
    type,
    row,
    index,
    dynamicClassName = '',
  }: {
    field: string;
    type: string;
    row: any;
    index?: number;
    dynamicClassName?: string;
  }) => {
    const abilityId = getPermissionAbilityId(field);
    const isPermissionField =
      type === 'boolean' &&
      abilityId !== undefined &&
      Object.prototype.hasOwnProperty.call(row, field);

    return (
      <>
        {type === 'clickableString' ? (
          <span
            className="table-clickable-field"
            onClick={() => {
              onFieldClick?.(row, field);
            }}
          >
            <TruncatedText text={row[field]} />
          </span>
        ) : isPermissionField ? (
          <button
            type="button"
            className={`table-permission-toggle ${
              row[field] ? 'permission-granted' : 'permission-denied'
            }`}
            aria-label={`${row[field] ? 'Disable' : 'Enable'} ${field} permission`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isPermissionToggleEnabled) return;

              handleUpdatePermission?.({
                permissionId: index,
                abilityId,
                status: !row[field],
              });
            }}
          >
            {!isPermissionToggleEnabled
              ? row[field]
              : row[field] || <CircleCloseIcon width={16} height={16} />}
          </button>
        ) : (
          <div className={dynamicClassName}>
            <TruncatedText text={row[field]} />
          </div>
        )}
      </>
    );
  };

  const buttonsDropDownOptions = (
    rowId: idType,
    buttons: {
      view: boolean;
      edit: boolean;
      audit: boolean;
    }
  ) => [
    ...(abilities?.includes('Edit') && buttons.edit
      ? [
          {
            item: (
              <>
                <PenIcon width={16} height={16} /> Edit
              </>
            ),
            closeWhenClicked: true,
            onSelect: () => {
              openModal?.(modalName, rowId);
              onEditClick?.(rowId);
              handleSelectedRow && setSelectedItem(rowId);
            },
          },
        ]
      : []),
    ...(abilities?.includes('Audit') && buttons.audit
      ? [
          {
            item: (
              <>
                <SearchFileIcon width={16} height={16} /> Audit
              </>
            ),
            closeWhenClicked: true,
            onSelect: () => onAuditClick?.(rowId, 'auditLog', tableName),
          },
        ]
      : []),
  ];

  const statusDropDownOptions = (id: idType, value: boolean) => [
    {
      item: <div className="status_item">Active</div>,
      closeWhenClicked: true,
      onSelect: () => {
        statusSetter && !value && statusSetter(id, true);
      },
    },
    {
      item: <div className="status_item">Inactive</div>,
      closeWhenClicked: true,
      onSelect: () => statusSetter && value && statusSetter(id, false),
    },
  ];

  const headerDropDownOptions = () => {
    const result: Array<{
      item: JSX.Element;
      closeWhenClicked: boolean;
      onSelect: () => void;
    }> = [];
    columns.forEach((el) => {
      if (el.headerName !== 'checkboxes') {
        result.push({
          item: (
            <div>
              <ControlledCheckbox
                name={el.field}
                title={el.headerName}
                control={control}
                dataTestId={`dynamic-added-item-${el.headerName}`}
              />
            </div>
          ),
          closeWhenClicked: false,
          onSelect: () => {},
        });
      }
    });
    return result;
  };

  return (
    <div
      ref={tableContainerRef}
      className={`table-container ${tableIsBig ? 'big_table' : ''} ${tableCustomClassName || ''}`}
      data-test-id={dataTestId}
    >
      {showInitialSkeleton ? (
        <TableSkeleton
          columns={columnOrder?.length || undefined}
          rows={size || undefined}
          showPagination={showPagination}
          {...skeletonProps}
        />
      ) : (
        <div
          className={`custom-table-wrapper ${isRefreshing || isUpdating ? 'is-refreshing' : ''}`}
        >
          <div
            ref={tableScrollRef}
            className={`t-scroll ${isDraggingColumn ? 'no-horizontal-scroll' : ''}`}
          >
            <table
              className={`custom-table ${isUpdating || isRefreshing ? 'blured' : ''} ${className}`}
            >
              <DragDropContext
                onBeforeDragStart={() => {
                  setIsDraggingColumn(true);
                }}
                onDragUpdate={(update) => {
                  if (!update.source) return;
                  setDragOverIndex(update.source.index);
                }}
                onDragEnd={(result) => {
                  setIsDraggingColumn(false);
                  setDragOverIndex(null);
                  onColumnDragEnd(result);
                }}
              >
                <Droppable
                  droppableId="columns"
                  direction="horizontal"
                  type="column"
                  isDropDisabled={isResizing}
                >
                  {(provided) => (
                    <thead
                      className="thead-title"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {isDraggingColumn && dragOverIndex === 0 && (
                        <div className="first-column-guide" />
                      )}
                      <tr className="t-row">
                        {columnOrder?.map((col: Column, index: number) => {
                          const isSorted =
                            sortingTable?.sortBy === col.sortingField;
                          const isNonEditableColumn = [
                            'edit',
                            'audit',
                            'view',
                            'viewImage',
                          ].includes(col.field);

                          const showGuide =
                            isDraggingColumn && dragOverIndex === index;

                          return (
                            <Draggable
                              key={col.field}
                              draggableId={col.field}
                              index={index}
                              isDragDisabled={isResizing || isNonEditableColumn}
                            >
                              {(draggableProvided) => (
                                <th
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  className={`sortable-header ${col.className} ${showGuide ? 'drag-guide' : ''}`}
                                  style={{
                                    ...draggableProvided.draggableProps.style,
                                    // width: col.headerName.length * 6,
                                  }}
                                  onClick={() => handleSorting(col)}
                                  onMouseEnter={() => setHoveredCol(col.field)}
                                  onMouseLeave={() => setHoveredCol(null)}
                                >
                                  <div
                                    className="drag-handle"
                                    {...draggableProvided.dragHandleProps}
                                    onMouseDown={(e) => {
                                      if (isResizing) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }
                                    }}
                                  >
                                    {col.headerName !== 'checkboxes' ? (
                                      <>
                                        {col.headerName}
                                        {col.isSortable &&
                                        method !== 'GET' &&
                                        !isNonEditableColumn &&
                                        (isSorted ||
                                          hoveredCol === col.field) ? (
                                          <SortingArrowIcon
                                            width={16}
                                            height={16}
                                            isAsc={
                                              isSorted
                                                ? sortingTable?.sortDir ===
                                                  'asc'
                                                  ? 'asc'
                                                  : 'desc'
                                                : 'asc'
                                            }
                                          />
                                        ) : null}
                                      </>
                                    ) : (
                                      <CustomDropdown
                                        button={<TableButtonsIcon />}
                                        buttonClassName="table-buttons-trigger"
                                        dropdownClassName="table_checkbox_options"
                                        options={headerDropDownOptions()}
                                      />
                                    )}
                                  </div>
                                </th>
                              )}
                            </Draggable>
                          );
                        })}
                      </tr>
                    </thead>
                  )}
                </Droppable>
              </DragDropContext>

              {hasTableData && (
                <tbody>
                  {rows?.map((row, index: number) => {
                    const hasViewField = columnOrder?.some(
                      (col) => col.field === 'view'
                    );
                    return (
                      <tr
                        key={row.id}
                        className={`draggable-row ${selectedItem === row.id ? 'active' : ''}`}
                        onClick={
                          () => {
                            handleSelectedRow && handleSelectedRow(row.id);
                          }
                          // hasViewField
                          //   ? undefined
                          //   : () =>
                          //       handleSelectedRow && handleSelectedRow(row.id)
                        }
                      >
                        {columnOrder?.map((col) => (
                          <td
                            key={col.field}
                            className={
                              col.type === 'status' || col.type === 'buttons'
                                ? 'non_hidden'
                                : ''
                            }
                          >
                            {col.type === 'node-render' ? (
                              col?.renderCell(row)
                            ) : col.field === 'viewImage' ? (
                              // && abilities?.includes('Document')
                              <button
                                onClick={() => {
                                  onImageClick?.(
                                    row[col.field],
                                    row.uploadedBy
                                  );
                                }}
                                disabled
                                className={`my-table-button ${!abilities?.includes('Document') ? 'btn_disable' : ''}`}
                              >
                                <ForbiddenIcon />
                              </button>
                            ) : col.field === 'view' ? (
                              <div
                                className="table-view-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectedRow &&
                                    handleSelectedRow(row.id);
                                }}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                }}
                              >
                                <ViewIcon />
                              </div>
                            ) : col.type === 'checkbox' ? (
                              !!row[col.field] ? (
                                <CheckCircleOutlineIcon />
                              ) : (
                                <CircleCloseIcon />
                              )
                            ) : col.type === 'buttons' &&
                              Object.values(col.buttons).some((el) => el) ? (
                              <CustomDropdown
                                button={<TableButtonsIcon />}
                                buttonClassName="table-buttons-trigger"
                                options={buttonsDropDownOptions(
                                  row.id,
                                  col.buttons
                                )}
                              />
                            ) : col.type === 'avatar' ? (
                              <div className="table_avatar">
                                {row?.[col.field]?.hasOwnProperty('icon') ? (
                                  <div className="table_avatar_icon">
                                    {row?.[col.field]?.icon}
                                  </div>
                                ) : (
                                  <div className="image_wrapper">
                                    <StatusWrapper
                                      size={10}
                                      statusColor={normalizeOnlineStatus(
                                        row?.[col.field]?.status
                                      )}
                                    >
                                      <img
                                        alt="table avatar image"
                                        src={
                                          row?.[col.field]?.image
                                            ? `${IMAGE_STORAGE_URL}/${row?.[col.field].image}`
                                            : FALLBACK_IMAGE
                                        }
                                        onError={handleImgError}
                                      />
                                    </StatusWrapper>
                                  </div>
                                )}
                                <div className="table_avatar_info">
                                  <div className="table_avatar_fullName">
                                    <TruncatedText
                                      text={`${row?.[col.field].firstName} ${row?.[col.field].lastName ? row?.[col.field].lastName : ''}`}
                                    />
                                  </div>
                                  <div className="table_avatar_relation">
                                    <h4>{row?.[col.field].permission}</h4>
                                  </div>
                                </div>
                              </div>
                            ) : col.type === 'status' ? (
                              <div className="table_status">
                                {row.isLocked && (
                                  <span className="lock_icon_wrapper">
                                    <LockIcon
                                      width={16}
                                      height={16}
                                      color="#FFF"
                                    />
                                  </span>
                                )}
                                <CustomDropdown
                                  button={
                                    <div
                                      className={`${
                                        row?.[col.field] ? 'active' : 'inactive'
                                      } status_dropdown`}
                                    >
                                      {row?.[col.field] ? 'Active' : 'Inactive'}
                                      <ArrowDownIcon width={16} height={16} />
                                    </div>
                                  }
                                  options={statusDropDownOptions(
                                    row.id,
                                    row?.[col.field]
                                  )}
                                />
                                {handleDeleteRow && !row?.isMemberContact && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteRow(row.id);
                                    }}
                                    className="close-button"
                                  >
                                    <CloseIcon
                                      width={16}
                                      height={16}
                                      color="#FFFFFF"
                                    />
                                  </button>
                                )}
                              </div>
                            ) : (
                              renderColField({
                                field: col.field,
                                type: col.type,
                                row,
                                index,
                                dynamicClassName:
                                  col.className === 'type_styles__admin'
                                    ? col.className
                                    : '',
                              })
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
            {!hasTableData && (
              <div className="table-no-rows no-data-found">No Data Found</div>
            )}
          </div>

          {showPagination && (
            <Pagination
              size={size}
              totalElements={totalElements}
              itemsCount={rows.length}
              currentPage={updatedPage + 1}
              handlePageChange={handlePageChange}
              handleSizeChange={handleSizeChange}
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
              dataTestId={dataTestId}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default memo(TableDynamic);
